/**
 * MultiDatePicker – velg flere (ikke nødvendigvis sammenhengende) datoer.
 * Navigerbar måned, klikk for å toggle. Returnerer string[] av YYYY-MM-DD.
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }

const TYPE_COLOR = {
  sfs: "#E63946",
  mass: "#4DA8DA",
  l4l: "#F4B400",
  story_swap: "#46B85B",
};

export default function MultiDatePicker({ value = [], onChange, minDate, dotsByDate = null, onMonthChange = null, "data-testid": testId }) {
  const { t } = useTranslation();
  const today = new Date();
  const [cursor, setCursor] = useState(() => startOfMonth(value.length > 0 ? new Date(value[0]) : today));
  const min = minDate ? new Date(minDate) : new Date(today.getFullYear(), today.getMonth(), today.getDate());
  min.setHours(0, 0, 0, 0);

  const selected = new Set(value);

  // Bygg dagene i grid (mandag-start)
  const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const startWeekday = (firstOfMonth.getDay() + 6) % 7; // mandag = 0
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));

  const changeMonth = (newDate) => {
    setCursor(newDate);
    if (onMonthChange) {
      const m = `${newDate.getFullYear()}-${pad(newDate.getMonth() + 1)}`;
      onMonthChange(m);
    }
  };

  const toggle = (date) => {
    const key = ymd(date);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next).sort());
  };

  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const weekdays = [t("collab.wd_mon"), t("collab.wd_tue"), t("collab.wd_wed"), t("collab.wd_thu"), t("collab.wd_fri"), t("collab.wd_sat"), t("collab.wd_sun")];

  return (
    <div className="panel-tight p-3" data-testid={testId || "multi-date-picker"}>
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => changeMonth(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          className="p-1 text-[var(--cream-faint)] hover:text-[var(--magenta)]" data-testid="mdp-prev">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="font-fraunces italic text-[var(--cream)] capitalize">{monthLabel}</div>
        <button onClick={() => changeMonth(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          className="p-1 text-[var(--cream-faint)] hover:text-[var(--magenta)]" data-testid="mdp-next">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdays.map((w) => (
          <div key={w} className="font-meta text-[9px] tracking-[0.1em] text-[var(--cream-faint)] text-center">{w}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const key = ymd(d);
          const isPast = d < min;
          const isSelected = selected.has(key);
          const dots = dotsByDate ? (dotsByDate[key] || []) : null;
          return (
            <button key={key} disabled={isPast} onClick={() => toggle(d)}
              className={`relative aspect-square text-xs border transition-colors ${
                isPast
                  ? "border-white/5 text-[var(--cream-faint)]/30 cursor-not-allowed"
                  : isSelected
                    ? "border-[var(--magenta)] bg-[var(--magenta)] text-white"
                    : "border-white/10 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
              }`}
              data-testid={`mdp-day-${key}`}>
              {d.getDate()}
              {dots && dots.length > 0 && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {dots.slice(0, 4).map((tp) => (
                    <span key={tp} className="block w-1 h-1 rounded-full"
                      style={{ backgroundColor: TYPE_COLOR[tp] || "var(--cream-faint)" }}
                      title={tp.toUpperCase()}
                    />
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selected.size > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)]">
              {t("collab.selected_dates")} ({selected.size})
            </span>
            <button onClick={() => onChange([])}
              className="font-meta text-[10px] text-[var(--magenta)] inline-flex items-center gap-1"
              data-testid="mdp-clear">
              <X className="h-3 w-3" /> {t("collab.clear")}
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {Array.from(selected).sort().map((d) => (
              <span key={d} className="inline-flex items-center gap-1 px-2 py-0.5 border border-[var(--magenta)]/40 text-[var(--magenta)] text-xs">
                {d}
                <button onClick={() => toggle(new Date(d))} className="hover:text-red-300">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
